import React from 'react';
import { MessageCircle, ClipboardCheck, TrendingUp, Trophy } from 'lucide-react';

const ApexHowItWorks = () => {
  const steps = [
    {
      id: 'step-1',
      number: '01',
      icon: MessageCircle,
      title: 'Free Discovery Audit',
      description: 'We analyse your current sales process, identify bottlenecks, and show you exactly where you are leaving money on the table.'
    },
    {
      id: 'step-2',
      number: '02',
      icon: ClipboardCheck,
      title: 'Custom Action Plan',
      description: 'Based on our audit, we create a tailored roadmap specifically for your gym — not a generic template that works for everyone.'
    },
    {
      id: 'step-3',
      number: '03',
      icon: TrendingUp,
      title: '1-2-1 Implementation',
      description: 'We work alongside you to implement the changes. Regular coaching calls, real-time feedback, and hands-on support.'
    },
    {
      id: 'step-4',
      number: '04',
      icon: Trophy,
      title: 'Ongoing Optimisation',
      description: 'Sales is never "done." We continuously refine your approach, track results, and optimise for maximum conversion.'
    }
  ];

  return (
    <section id="how-it-works" className="relative py-20 md:py-28 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="text-[#22C55E] text-sm font-medium tracking-[0.2em] uppercase">
            How It Works
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
            Your Path to Confident Sales
          </h2>
          <p className="text-[#A1A1AA] mt-4 max-w-2xl mx-auto">
            A proven process that transforms gym owners from dreading sales calls 
            to looking forward to them.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connection line - desktop */}
          <div className="hidden lg:block absolute top-24 left-[calc(12.5%+24px)] right-[calc(12.5%+24px)] h-px bg-gradient-to-r from-[#22C55E]/50 via-[#22C55E] to-[#22C55E]/50" />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div
                key={step.id}
                data-testid={step.id}
                className="relative group"
              >
                {/* Card */}
                <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 h-full hover:border-[#22C55E]/50 transition-all duration-300">
                  {/* Step number */}
                  <div className="relative z-10 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#22C55E] flex items-center justify-center relative">
                      <span className="text-black font-bold text-sm">{step.number}</span>
                      {/* Pulse effect */}
                      <div className="absolute inset-0 rounded-full bg-[#22C55E] animate-ping opacity-20" />
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="w-14 h-14 rounded-xl bg-[#22C55E]/10 flex items-center justify-center mb-4 group-hover:bg-[#22C55E]/20 transition-colors">
                    <step.icon className="w-7 h-7 text-[#22C55E]" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Arrow connector - mobile/tablet */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-center py-4">
                    <div className="w-px h-8 bg-gradient-to-b from-[#22C55E] to-[#22C55E]/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApexHowItWorks;
